import * as React from 'react';
import cn from 'classnames';
import { useEffect, useState } from 'react';

import { useTheme } from '../theme';
import { minusIcon, plusIcon } from './Icons';

interface Props {
	minValue?: number;
	maxValue?: number;
	value: number;
	readonly?: boolean;
	isIncreaseDisabled?: boolean;
	isDecreaseDisabled?: boolean;
	onIncrease?: () => void;
	onDecrease?: () => void;
	showOnlyIncreaseButtonWhenValueIsNull?: boolean;
	valueClassName?: string;
	classes?: {
		root?: string;
		minus?: string;
		plus?: string;
		value?: string;
	};
}

const Counter: React.FunctionComponent<Props> = props => {
	const { Counter: theme } = useTheme('Counter');
	const [antiBlinkingValue, setAntiBlinkingValue] = useState(0);
	const canInc = React.useMemo(() => {
		return (
			!props.isIncreaseDisabled &&
			(props.maxValue === undefined || props.value < props.maxValue) &&
			!props.readonly
		);
	}, [props.value, props.maxValue, props.readonly, props.isIncreaseDisabled]);

	const canDec = React.useMemo(() => {
		return (
			(!props.isDecreaseDisabled && props.value > 0 && props.minValue === undefined) ||
			props.value > props.minValue
		);
	}, [props.value, props.minValue, props.isDecreaseDisabled]);

	useEffect(() => {
		if (typeof props.value !== 'undefined' && antiBlinkingValue !== props.value) {
			setAntiBlinkingValue(props.value);
		}
	}, [props.value]);

	return (
		<div className={cn(theme.counter, props.classes?.root)}>
			<button
				type="button"
				data-test-id="counter-minus"
				className={cn(theme.button, props.classes?.minus, {
					[theme.button_disabled]: !canDec,
					[theme.button_readonly]:
						!canDec &&
						(props.readonly || (props.showOnlyIncreaseButtonWhenValueIsNull && props.value === 0))
				})}
				onClick={canDec ? props.onDecrease : () => {}}
			>
				{minusIcon}
			</button>

			<div
				data-test-id="counter-value"
				className={cn(theme.value, props.valueClassName, props.classes?.value, {
					[theme.value_zero]: props.value === 0,
					[theme.value_hidden]: props.showOnlyIncreaseButtonWhenValueIsNull && props.value === 0
				})}
			>
				{antiBlinkingValue}
			</div>

			<button
				type="button"
				data-test-id="counter-plus"
				className={cn(theme.button, props.classes?.plus, {
					[theme.button_disabled]: !canInc,
					[theme.button_readonly]: props.readonly
				})}
				onClick={canInc ? props.onIncrease : () => {}}
			>
				{plusIcon}
			</button>
		</div>
	);
};

export default Counter;
